import Logo from '@/assets/logo-white.svg'
import logoutIcon from '@/assets/logout.svg'
import { Confirm } from '@/components/main/Confirm'
import { useAuthContext, useSubscriptionContext } from '@/context'
import { useCart } from '@/context/cart'
import { useMiniCart } from '@/context/mini-cart'
import { generalItems, isAdvancedNavigationUser, menuItems, type NavigationVisibility } from '@/layout/const'
import { useLogout } from '@/services/auth/mutations'
import { isDev, isProd, isStaging } from '@/utils/env'
import { ChevronDown, CircleUser, Menu, ShoppingBag, X, Zap } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { MainNav } from './MainNav'
import { UserNav } from './UserNav'
import AgentLauncher from '@/modules/agent/components/AgentLauncher'

type Props = {
    isCollapsed: boolean
}

export default function Header({ isCollapsed }: Props) {
    const { user } = useAuthContext()
    const { state } = useCart()
    const { setIsOpen: setMiniCartOpen } = useMiniCart()
    const { subscription } = useSubscriptionContext()
    const location = useLocation()
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
    const { mutateAsync: logoutUser } = useLogout({})
    const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({})
    const canSeeAdvancedNavigation = isAdvancedNavigationUser(user)

    const navigateLogout = () => {
        localStorage.clear()
        logoutUser({})
        window.location.reload()
    }

    const shouldShowItem = (environments: string[]) => {
        if (isDev && environments.includes('development')) return true
        if (isStaging && environments.includes('staging')) return true
        if (isProd && environments.includes('production')) return true
        return false
    }

    const isActiveRoute = (match: string[]) => {
        const currentPath = location.pathname.split('/').slice(0, 3).join('/')
        return match.some(route => currentPath.startsWith(`/dashboard/${route}`))
    }

    const toggleSection = (id: string) => {
        setExpandedSections(prev => ({
            ...prev,
            [id]: !prev[id],
        }))
    }

    const totalCartItems = state.items.reduce((sum, item) => sum + item.quantity, 0)

    useEffect(() => {
        if (isMobileMenuOpen) {
            document.body.style.overflow = 'hidden'
        } else {
            document.body.style.overflow = 'auto'
        }

        // Cleanup function to remove the event listener
        return () => {
            document.body.style.overflow = 'auto'
        }
    }, [isMobileMenuOpen])

    return (
        <div className='relative'>
            <div
                className={`flex flex-col fixed h-[64px] w-full z-30 bg-white ${
                    isCollapsed ? 'md:w-[calc(100%-80px)]' : 'md:w-[calc(100%-250px)]'
                }`}
            >
                <div className='border-b'>
                    <div className='flex items-center h-16 px-4'>
                        <MainNav className='sm:mx-6 md:flex' />
                        <div className='flex items-center justify-end w-full ml-auto space-x-4 md:justify-between md:w-auto'>
                            <div className='flex items-center gap-2 sm:gap-4'>
                                <AgentLauncher />

                                <span
                                    className='relative p-1.5 sm:p-2 bg-white rounded-full shadow-md cursor-pointer'
                                    onClick={() => setMiniCartOpen(true)}
                                >
                                    <ShoppingBag size={16} className='sm:hidden' />
                                    <ShoppingBag size={18} className='hidden sm:block' />
                                    <span className='absolute -top-1.5 -right-1.5 sm:-top-2 sm:-right-2 flex items-center justify-center w-4 h-4 sm:w-5 sm:h-5 text-[10px] sm:text-xs font-bold text-white bg-[#A862B4] rounded-full'>
                                        {totalCartItems}
                                    </span>
                                </span>

                                <UserNav />

                                {(user?.isAdmin || user?.email?.toLowerCase().endsWith('@500rockets.io')) && (
                                    <Link
                                        className='hidden sm:block font-bold text-primary-main'
                                        to='/dashboard/admin'
                                        title='Admin / Backend'
                                    >
                                        <CircleUser size={26} />
                                    </Link>
                                )}

                                <div
                                    className='z-30 md:hidden'
                                    onClick={() => {
                                        setIsMobileMenuOpen(!isMobileMenuOpen)
                                    }}
                                >
                                    {isMobileMenuOpen ? <X size={26} className='text-white' /> : <Menu size={26} />}
                                </div>
                            </div>

                            {/* Slide-in mobile menu */}
                            <div
                                className={`fixed top-0 right-0 w-72 h-full bg-primary-main z-20 transition-all duration-500 ease-in-out overflow-auto ${
                                    isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
                                }`}
                            >
                                <div className='flex items-center justify-center w-full mt-8 mb-6'>
                                    <img src={Logo} className='text-center' />
                                </div>

                                <div className='flex flex-col gap-1 px-4 mt-12'>
                                    <hr className='h-[1px] bg-[#5E5E5E] w-full my-1' />

                                    {menuItems
                                        .filter(item => {
                                            const navigationVisibility = (
                                                item as unknown as { navigationVisibility?: NavigationVisibility }
                                            ).navigationVisibility
                                            if (navigationVisibility === 'parked') return false
                                            if (navigationVisibility !== 'core' && !canSeeAdvancedNavigation)
                                                return false
                                            return (
                                                (!item.isAdminRoute ||
                                                    (item.isAdminRoute &&
                                                        (user?.isAdmin ||
                                                            user?.email?.toLowerCase().endsWith('@500rockets.io')))) &&
                                                shouldShowItem(item.environments)
                                            )
                                        })
                                        .map(({ icon, name, link, id_element, match, subItems }) => {
                                            const Icon =
                                                typeof icon === 'string'
                                                    ? ({ className }: { className?: string }) => (
                                                          <img src={icon} className={className} alt={name} />
                                                      )
                                                    : (icon as React.ComponentType<{
                                                          className?: string
                                                          strokeWidth?: number
                                                      }>)
                                            const hasSubItems = Boolean(subItems?.length)
                                            const isExpanded = expandedSections[id_element] ?? false
                                            const isRouteActive = isActiveRoute(match)

                                            return (
                                                <div key={id_element}>
                                                    {hasSubItems ? (
                                                        <>
                                                            <div
                                                                className={`flex items-center w-full h-12 px-4 text-[#A4A4A4] rounded-xl hover:text-white transition-all duration-500 cursor-pointer ${
                                                                    isRouteActive && !isExpanded
                                                                        ? `bg-[#63316B] text-white`
                                                                        : 'text-[#A4A4A4]'
                                                                }`}
                                                                onClick={() => toggleSection(id_element)}
                                                            >
                                                                <Icon
                                                                    className='w-[18px] h-[18px] flex-shrink-0'
                                                                    strokeWidth={1.75}
                                                                />
                                                                <span className='ml-4 text-sm font-medium'>{name}</span>
                                                                <ChevronDown
                                                                    className={`ml-auto w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                                                                />
                                                            </div>
                                                            {isExpanded && (
                                                                <div className='mt-1 ml-10 space-y-2'>
                                                                    {subItems?.map((item, idx) => (
                                                                        <Link
                                                                            key={idx}
                                                                            to={item.link}
                                                                            onClick={() => setIsMobileMenuOpen(false)}
                                                                            className={`block text-sm rounded-lg text-[#E5E5E5] hover:text-white transition-all duration-150 py-2 px-2 ${
                                                                                location.pathname.startsWith(item.link)
                                                                                    ? `bg-[#63316B] text-white`
                                                                                    : ''
                                                                            }`}
                                                                        >
                                                                            {item.name}
                                                                        </Link>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </>
                                                    ) : (
                                                        <Link
                                                            to={link}
                                                            onClick={() => setIsMobileMenuOpen(false)}
                                                            className={`flex items-center w-full h-12 px-4 rounded-xl hover:text-white duration-500 transition-all ${
                                                                isRouteActive
                                                                    ? `bg-[#63316B] text-white`
                                                                    : 'text-[#A4A4A4]'
                                                            }`}
                                                            id={id_element}
                                                        >
                                                            <Icon
                                                                className='w-[18px] h-[18px] flex-shrink-0'
                                                                strokeWidth={1.75}
                                                            />
                                                            <span className='ml-4 text-sm font-medium'>{name}</span>
                                                        </Link>
                                                    )}
                                                </div>
                                            )
                                        })}

                                    <hr className='h-[1px] bg-[#5E5E5E] w-full my-4' />

                                    {/* General Items */}
                                    {generalItems
                                        .filter(item => {
                                            const navigationVisibility = (
                                                item as unknown as {
                                                    navigationVisibility?: NavigationVisibility
                                                }
                                            ).navigationVisibility
                                            if (navigationVisibility === 'parked') return false
                                            if (navigationVisibility !== 'core' && !canSeeAdvancedNavigation)
                                                return false
                                            return (
                                                (!item.isAdminRoute ||
                                                    (item.isAdminRoute &&
                                                        (user?.isAdmin ||
                                                            user?.email?.toLowerCase().endsWith('@500rockets.io')))) &&
                                                shouldShowItem(item.environments)
                                            )
                                        })
                                        .map(({ icon, name, link, id_element, match }) => {
                                            const Icon =
                                                typeof icon === 'string'
                                                    ? ({ className }: { className?: string }) => (
                                                          <img src={icon} className={className} alt={name} />
                                                      )
                                                    : (icon as React.ComponentType<{
                                                          className?: string
                                                          strokeWidth?: number
                                                      }>)
                                            const isRouteActive = isActiveRoute(match)

                                            return (
                                                <Link
                                                    key={id_element}
                                                    to={link}
                                                    onClick={() => setIsMobileMenuOpen(false)}
                                                    className={`flex items-center w-full h-12 px-4 rounded-xl hover:text-white duration-500 transition-all ${
                                                        isRouteActive ? `bg-[#63316B] text-white` : 'text-[#A4A4A4]'
                                                    }`}
                                                    id={id_element}
                                                >
                                                    <Icon
                                                        className='w-[18px] h-[18px] flex-shrink-0'
                                                        strokeWidth={1.75}
                                                    />
                                                    <span className='ml-4 text-sm font-medium'>{name}</span>
                                                </Link>
                                            )
                                        })}

                                    <Confirm
                                        trigger={
                                            <div className='flex items-center w-full h-12 px-4 text-[#A862B4] rounded hover:text-white transition-all duration-150 '>
                                                <img src={logoutIcon} alt='' />

                                                <span className={`ml-4 text-base font-medium `}>Log out</span>
                                            </div>
                                        }
                                        title='Log out?'
                                        message='Are you sure you want to log out?'
                                        primaryAction={navigateLogout}
                                    />

                                    {!subscription?.active && (
                                        <Link
                                            to={`?upgrade=true`}
                                            className='flex items-center w-full h-12 px-4 text-[#A862B4] rounded hover:text-white transition-all duration-150'
                                            onClick={() => setIsMobileMenuOpen(false)}
                                        >
                                            <span className='text-xl'>
                                                <Zap />
                                            </span>
                                            <span className={`ml-4 text-base font-medium `}>Upgrade</span>
                                        </Link>
                                    )}
                                </div>
                            </div>

                            {/* Bg Overlay when mobile menu is open */}
                            {isMobileMenuOpen && (
                                <div
                                    style={{ margin: 0 }}
                                    className='fixed top-0 bottom-0 left-0 right-0 z-10 w-full h-full m-0 bg-black opacity-50'
                                    onClick={() => {
                                        setIsMobileMenuOpen(false)
                                    }}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
